import { Row, Col, Card, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { trackExternalLink, trackButtonClick, trackFileDownload, trackSectionView } from '../utils/analytics';

function ContactCard({ item, t, isEmail }) {
  const [copied, setCopied] = useState(false);

  const handleEmailClick = (e) => {
    if (isEmail) {
      e.preventDefault();
      navigator.clipboard.writeText(item.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      // Rastrear cópia de email
      trackButtonClick('Copy Email', 'contact-section');
    }
  };

  const cardContent = (
    <Card
      className="h-100 contact-card"
      onClick={isEmail ? handleEmailClick : undefined}
      style={{
        background: 'rgba(0, 0, 0, 0.3)',
        border: `1px solid ${item.color}40`,
        borderRadius: '0.75rem',
        cursor: 'pointer',
        transition: 'all 0.3s',
        overflow: 'hidden'
      }}
    >
      <Card.Body className="text-center p-3">
        <div
          style={{
            fontSize: '2.5rem',
            marginBottom: '0.5rem',
            filter: 'drop-shadow(0 0 8px ' + item.color + '80)',
            color: item.color
          }}
        >
          <i className={`bi ${item.icon}`}></i>
        </div>
        <Card.Title
          style={{
            fontSize: '0.9rem',
            color: item.color,
            fontWeight: '600',
            marginBottom: '0.5rem'
          }}
        >
          {t(`contact.${item.titleKey}`)}
        </Card.Title>
        <Card.Text
          style={{
            fontSize: '0.85rem',
            color: 'var(--text-light)',
            margin: 0,
            wordBreak: 'break-word'
          }}
        >
          {item.content}
        </Card.Text>
      </Card.Body>
    </Card>
  );

  if (isEmail) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-email`}>
            {copied ? t('contact.emailCopied') : t('contact.emailCopy')}
          </Tooltip>
        }
      >
        {cardContent}
      </OverlayTrigger>
    );
  }

  if (item.link) {
    return (
      <a
        href={item.link}
        target={item.link.startsWith('http') || item.link.startsWith('/') ? '_blank' : undefined}
        rel={item.link.startsWith('http') || item.link.startsWith('/') ? 'noopener noreferrer' : undefined}
        style={{ textDecoration: 'none', display: 'block' }}
        onClick={() => {
          // Rastrear clique em link
          if (item.link.startsWith('http')) {
            trackExternalLink(item.link, t(`contact.${item.titleKey}`));
          } else if (item.link.endsWith('.pdf')) {
            trackFileDownload(item.content, 'pdf');
          }
        }}
      >
        {cardContent}
      </a>
    );
  }

  return cardContent;
}

export default function Contact() {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Rastrear visualização da seção de contato
  useEffect(() => {
    trackSectionView('Contact');
  }, []);

  // URLs dinâmicas baseadas no idioma
  const cvFileName = language === 'pt' ? 'CV_PT_Daniel_Marnet.pdf' : 'CV_EN_Daniel_Marnet.pdf';
  const whatsappMessage = language === 'pt' ? 'Ol%C3%A1!' : 'Hello%21';
  const whatsappUrl = `https://wa.me/5512991876096?text=${whatsappMessage}`;

  const contactItems = [
    {
      icon: 'bi-geo-alt',
      titleKey: 'location',
      content: 'São José dos Campos - SP, Brasil',
      link: 'https://maps.app.goo.gl/oE3PJbebGgJ1t3PT7',
      color: '#00f5ff'
    },
    {
      icon: 'bi-phone',
      titleKey: 'phone',
      content: '+55 12 99187-6096',
      link: whatsappUrl,
      color: '#00ff88'
    },
    {
      icon: 'bi-envelope',
      titleKey: 'email',
      content: 'daniel.marnet.tech@gmail.com',
      link: 'mailto:daniel.marnet.tech@gmail.com',
      color: '#ff6b6b'
    },
    {
      icon: 'bi-file-earmark-pdf',
      titleKey: 'cv',
      content: language === 'pt' ? 'CV_PT_Daniel_Marnet.pdf' : 'CV_EN_Daniel_Marnet.pdf',
      link: `/${cvFileName}`,
      color: '#ffffff'
    },
    {
      icon: 'bi-linkedin',
      titleKey: 'linkedin',
      content: 'linkedin.com/in/daniel-marnet',
      link: 'https://linkedin.com/in/daniel-marnet',
      color: '#0077b5'
    },
    {
      icon: 'bi-github',
      titleKey: 'github',
      content: 'github.com/daniel-marnet',
      link: 'https://github.com/daniel-marnet',
      color: '#6e5494'
    },
    {
      icon: 'bi-mortarboard',
      titleKey: 'lattes',
      content: t('contact.lattes') === 'Lattes' ? 'Currículo Lattes' : 'Lattes CV',
      link: 'https://lattes.cnpq.br/4526918868853717',
      color: '#ffa500'
    },
    {
      icon: 'bi-globe',
      titleKey: 'website',
      content: 'www.marnettech.com.br',
      link: 'https://www.marnettech.com.br/',
      color: '#00d4ff'
    }
  ];

  return (
    <section id="contact" className="panel">
      <div className="d-flex align-items-center mb-4">
        <div className="me-3" style={{ fontSize: '2.5rem' }}>
          <i className="bi bi-telephone" style={{ color: 'var(--accent)' }}></i>
        </div>
        <div>
          <h2 className="mb-2">{t('contact.title')}</h2>
          <p
            className="mb-0"
            style={{
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.85)',
              fontFamily: 'var(--font-body)',
              fontWeight: '400',
              lineHeight: '1.7',
              letterSpacing: '0.4px',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
            }}
          >
            {t('contact.description')}
          </p>
        </div>
      </div>

      <Row className="g-3">
        {contactItems.map((item, index) => {
          const isEmail = item.titleKey === 'email';
          return (
            <Col key={index} xs={12} sm={6} md={4} lg={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <ContactCard item={item} t={t} isEmail={isEmail} />
              </motion.div>
            </Col>
          );
        })}
      </Row>

      <style>{`
        .contact-card {
          transition: all 0.3s ease;
        }
        .contact-card:hover {
          border-color: var(--accent) !important;
          box-shadow: 0 0 20px rgba(0, 245, 255, 0.3);
          background: rgba(0, 245, 255, 0.05) !important;
        }
        .contact-card a {
          text-decoration: none;
        }
        .contact-card:hover .card-title {
          color: var(--accent) !important;
          text-shadow: 0 0 8px var(--accent);
        }
      `}</style>
    </section>
  );
}
